import { fmtUSD, fmtCarat } from "@/lib/format";

/**
 * Sticky footer row that shows aggregates for the rows currently displayed.
 * Pass an array of cell configs matching the table's column count (in order).
 *
 * cellConfig examples:
 *   { type: "sum-usd", get: r => r.amount }
 *   { type: "avg-usd", get: r => r.price_per_carat }
 *   { type: "sum-carat", get: r => r.total_carat }
 *   { type: "count" }          // shows number of rows
 *   { type: "label", text: "TOTAL" }
 *   null                       // empty cell (for icon/action columns)
 */
export const TableSummaryRow = ({ rows, cells, testid = "table-summary-row" }) => {
    const nRows = rows.length;
    return (
        <tfoot className="sticky bottom-0 z-10">
            <tr className="border-t-2 border-stone-900 bg-stone-100 font-medium text-[11px] uppercase tracking-widest text-stone-800" data-testid={testid}>
                {cells.map((c, idx) => {
                    if (!c) return <td key={idx} className="py-2.5 px-4" />;

                    if (c.type === "label") {
                        return <td key={idx} className={`py-2.5 px-4 ${c.className || ""}`}>{c.text}</td>;
                    }
                    if (c.type === "count") {
                        return <td key={idx} className="py-2.5 px-4 text-stone-600">{nRows} rows</td>;
                    }

                    const vals = rows.map(c.get).filter(v => v != null && !Number.isNaN(Number(v))).map(Number);
                    const sum = vals.reduce((a, b) => a + b, 0);
                    const avg = vals.length ? sum / vals.length : 0;

                    let display = "—";
                    let cls = "num text-stone-900";
                    if (vals.length) {
                        switch (c.type) {
                            case "sum-usd":
                                display = fmtUSD(sum);
                                cls += c.accent ? ` text-${c.accent}` : "";
                                break;
                            case "avg-usd":
                                display = fmtUSD(avg);
                                cls = "num text-stone-600 italic";
                                break;
                            case "sum-carat":
                                display = fmtCarat(sum);
                                break;
                            case "avg-carat":
                                display = fmtCarat(avg);
                                cls = "num text-stone-600 italic";
                                break;
                            case "sum":
                                display = String(sum);
                                break;
                            default:
                                display = String(sum);
                        }
                    }
                    return (
                        <td key={idx} className={`py-2.5 px-4 ${c.align === "left" ? "text-left" : "text-right"} ${cls}`} data-testid={c.testid}>
                            {display}
                        </td>
                    );
                })}
            </tr>
        </tfoot>
    );
};
